"use client";

import Image from "next/image";
import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import EnrollTourButtons from "@/components/cms/EnrollTourButtons";
import { useParallaxOffset } from "@/hooks/useParallaxOffset";

// Simple in-view hook for animation (once: never resets to false once visible)
function useInView(
  ref: { current: HTMLElement | null },
  options?: { margin?: string },
) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { rootMargin: options?.margin || "0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, options?.margin]);
  return inView;
}

export default function ContactHero({ cms, logoUrl }: { cms?: Record<string, string>; logoUrl?: string | null }) {
  const headingRef = useRef<HTMLDivElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const showHeading = useInView(headingRef, {
    margin: "0px",
  });

  // Use optimized parallax hook with proper settings
  const [offset, isDesktop, transform3d] = useParallaxOffset(0.2, 640, parallaxRef);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center font-nunito overflow-hidden">
      {/* Background Image with Parallax */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <div
          ref={parallaxRef}
          className="absolute inset-0 will-change-transform w-full h-[120%] -top-[10%]"
          style={mounted && isDesktop ? { transform: transform3d } : {}}
        >
          {cms?.bg_image_url && <Image
            src={cms.bg_image_url}
            alt="School building"
            fill
            sizes="100vw"
            className="object-cover"
            priority
            quality={80}
          />}
        </div>
        <div
          className="absolute inset-0 z-10"
          style={{ background: cms?.overlay_color ?? "rgba(6,22,82,0.6)" }}
        ></div>
      </div>
      {/* Content */}
      <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto flex flex-col items-center pt-12 pb-12 sm:pt-24 sm:pb-24">
        {logoUrl && (
        <div className="mb-8 pt-8">
          <Image
            src={logoUrl}
            alt="Ashton John's Private School Logo"
            width={200}
            height={200}
            className="mx-auto w-72 h-full"
            priority
            quality={80}
          />
        </div>
        )}
        <motion.div
          ref={headingRef}
          initial={{ opacity: 0, y: 60 }}
          animate={showHeading ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{ type: "spring", stiffness: 120, damping: 20 }}
          className="space-y-4 mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-2">{cms?.heading /* || "Contact Us" */}</h1>
          <p className="text-lg sm:text-xl md:text-2xl font-light leading-relaxed mb-2">
            {cms?.subheading /* || "We would love to hear from you and be a part of your child\u2019s education." */}
          </p>
        </motion.div>
        <EnrollTourButtons
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-2"
          buttonClassName="bg-school-gold hover:bg-school-gold-dark text-black font-bold py-4 px-8 rounded text-lg transition-colors text-center"
          enrollText={cms?.enrol_btn_text}
          tourText={cms?.tour_btn_text}
          enrollUrl={cms?.enrol_btn_url}
          tourUrl={cms?.tour_btn_url}
        />
      </div>
    </section>
  );
}
